import styled from "@emotion/styled";
import { createStyles } from "@material-ui/core/styles";
import withStyles from "@material-ui/core/styles/withStyles";
import { FullScreenDialog } from "components/common/Modal";
import {
  cvsInfo as cvsInfoMap,
  getCVSNote,
  HOUR_UNTIL_PURCHASE_PHONE_CANCELED,
  phoneInfo as phoneInfoMap,
} from "constants/purchase";
import { ConfirmCvsPaymentOrderDetails } from "features/ConfirmCvsPaymentOrderDetails";
import { ConfirmCvsPaymentOrderDetailsWithEvent } from "features/ConfirmCvsPaymentOrderDetailsWithEvent";
import { useI18n, I18nTrans } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";
import React, { Fragment } from "react";
import { LinkButton, modalWindowMixin } from "styles";
import { isPayPalPendingError } from "utility/paypal";

import type { Props as ViewProps } from "./confirmCvsPaymentViewProps";
import type { WithStyles } from "@material-ui/core/styles";
import type { StyleRules } from "@material-ui/core/styles/withStyles";
import type { CVSNote } from "constants/purchase";

const styles: StyleRules = createStyles({
  modalScroll: {
    position: "absolute",
    top: "10%",
    left: "10%",
    overflow: "scroll",
    height: "100%",
    display: "block",
  },
});

const _PaymentNumberContainer = styled.div`
  .contents_block {
    margin: 0 auto;
    width: 90%;
    @media screen and (min-width: 768px) {
      width: 680px;
    }
  }
  h2 {
    margin-bottom: 60px;
    font-size: 20px;
    text-align: center;
    font-weight: normal;
    @media screen and (min-width: 768px) {
      margin-bottom: 40px;
      font-size: 25px;
    }
  }
  .shipping_fee {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 10px;
    .heading {
      padding-bottom: 5px;
      font-size: 12px;
    }
    .total {
      .price_value {
        font-size: 14px;
      }
      span {
        margin-left: 5px;
        font-size: 12px;
        font-weight: normal;
      }
    }
  }
  .total_num {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 80px;
    .heading {
      padding-bottom: 5px;
      font-size: 12px;
    }
    .total {
      .total_price_value {
        font-size: 25px;
        font-weight: bold;
      }
      span {
        margin-left: 5px;
        font-size: 12px;
        font-weight: normal;
      }
    }
  }
  .payment_info_block {
    margin-bottom: 25px;
    .info {
      &:not(:last-child) {
        margin-bottom: 25px;
      }
      h3 {
        margin-bottom: 15px;
        line-height: 1.3em;
        letter-spacing: 1px;
      }
      p {
        letter-spacing: 1px;
        line-height: 1.3em;
      }
      a {
        color: #00c2ae;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
  .payment_Notes {
    margin-bottom: 50px;
    @media screen and (min-width: 768px) {
      margin-bottom: 100px;
    }
    h3 {
      margin-bottom: 15px;
      font-size: 15px;
      line-height: 1.3em;
      letter-spacing: 1px;
    }
    p {
      padding-left: 1em;
      margin-bottom: 8px;
      font-size: 15px;
      text-indent: -1em;
      line-height: 1.3em;
      letter-spacing: 1px;
    }
  }
`;

const PaymentNumberContainer = styled(_PaymentNumberContainer)(
  modalWindowMixin
);

type Props = ViewProps & WithStyles<typeof styles>;

/**
 * コンビニ・キャリア・PayPal決済の支払案内モーダルの描画部。
 * 注文の取得元（Firestoreの注文ドキュメント・購入履歴API）に依存しない素の値だけを受け取る
 */
const ConfirmCvsPaymentViewBase = ({
  close,
  cvsInfo,
  errorInfo,
  eventGroups,
  isOpen,
  items,
  paypalInfo,
  phoneInfo,
  serviceFee,
  settlementFee,
  shippingFee,
  status,
  classes,
}: Props) => {
  const { t } = useI18n();

  const cvsCode = cvsInfo?.cvsCode;
  const receiptNo = cvsInfo?.receiptNo;
  const confNo = cvsInfo?.confNo;
  const phoneCode = phoneInfo?.phoneCode;
  const phoneURL = phoneInfo?.phoneUrl;
  const paypalURL = paypalInfo?.paypalUrl;

  const cvsNote = cvsCode ? getCVSNote(cvsCode, confNo, receiptNo) : null;

  const totalPrice =
    items.reduce((acc, cur) => {
      return acc + cur.price * cur.count;
    }, 0) +
    shippingFee +
    serviceFee +
    settlementFee;

  const renderPhoneInfo = (status: string | null) => {
    return (
      <>
        <div className="payment_info_block">
          <div className="info">
            <h3>{t("confirmCvsPayment.designatedPaymentMethod")}</h3>

            <p>
              {phoneCode &&
                phoneCode !== "docomo" &&
                phoneInfoMap()?.[phoneCode]}
            </p>
          </div>

          {status === "UNPROCESS" ? (
            <div className="info">
              <p>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <I18nTrans i18nKey="confirmCvsPayment.paymentThisPage">
                  お支払いは
                  {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                  <a href={phoneURL}>こちら</a>のページからお願いします。
                </I18nTrans>
              </p>
              <p>{t("confirmCvsPayment.moveExternalSite")}</p>
              <p>
                {`${HOUR_UNTIL_PURCHASE_PHONE_CANCELED}${t("confirmCvsPayment.hourUntilPurchasePhoneCanceled")}`}
              </p>
            </div>
          ) : (
            <div className="info">
              <p>{t("confirmCvsPayment.note1")}</p>
              <p>
                {`${HOUR_UNTIL_PURCHASE_PHONE_CANCELED}${t("confirmCvsPayment.hourUntilPurchasePhoneCanceled")}`}
              </p>
            </div>
          )}
          <div className="info"></div>
        </div>
      </>
    );
  };

  const renderPayPalInfo = (
    status: string | null,
    errorInfo: string | null
  ) => {
    return (
      <>
        <div className="payment_info_block">
          <div className="info">
            <h3>{t("confirmCvsPayment.designatedPaymentMethod")}</h3>
            <p>PayPal</p>
          </div>

          {status === "UNPROCESS" && (
            <div className="info">
              {isPayPalPendingError(errorInfo) ? (
                <p>{t("confirmCvsPayment.paypalPendingReview")}</p>
              ) : (
                <Fragment>
                  <p>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <I18nTrans i18nKey="confirmCvsPayment.paymentThisPage">
                      お支払いは
                      {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                      <a href={paypalURL}>こちら</a>のページからお願いします。
                    </I18nTrans>
                  </p>
                  <p>{t("confirmCvsPayment.moveExternalSite")}</p>
                </Fragment>
              )}
            </div>
          )}
          <div className="info"></div>
        </div>
      </>
    );
  };

  const renderCvsInfo = (cvsNote: CVSNote) => {
    return (
      <>
        <div className="payment_info_block">
          <div className="info">
            <h3>{t("confirmCvsPayment.designatedConvenienceStore")}</h3>
            <p>{cvsCode && cvsInfoMap()[cvsCode]}</p>
          </div>

          <div className="info">
            <h3>{cvsNote.ReceiptNumberCaption}</h3>
            <p>{receiptNo}</p>
          </div>

          <div className="info">
            <h3>{cvsNote.ConfirmNumberCaption}</h3>
            <p>{confNo}</p>
          </div>
        </div>

        <div className="payment_Notes">
          <h3>{t("confirmCvsPayment.readBeforePayment")}</h3>
          {cvsNote.message.split("<br/>").map((msg, index) => {
            if (msg.startsWith("http") || msg.startsWith(" http")) {
              //httpスタートの文字の場合aタグでリンクを作成
              const link = msg.replace(" ", "");
              return (
                <p key={index}>
                  <a href={link} target="_blank" rel="noreferrer">
                    {msg}
                  </a>
                </p>
              );
            }
            return <p key={index}>{msg}</p>;
          })}
        </div>
      </>
    );
  };
  return (
    <FullScreenDialog
      className={classes.modalScroll}
      open={isOpen}
      onBackdropClick={close}
    >
      <PaymentNumberContainer>
        <div className="contents_block">
          <h2>{t("confirmCvsPayment.paymentDetails")}</h2>
          {eventGroups === null ? (
            <ConfirmCvsPaymentOrderDetails items={items} />
          ) : (
            eventGroups.map((group, index) => (
              <ConfirmCvsPaymentOrderDetailsWithEvent
                key={`details_item_${index}`}
                eventTitle={group.eventTitle}
                artists={group.artists}
                eventOpenDateFull={group.eventOpenDateFull}
                eventEndDateFull={group.eventEndDateFull}
                isLongEvent={group.isLongEvent}
                eventOpenDay={group.eventOpenDay}
                products={group.products}
              />
            ))
          )}
          {shippingFee ? (
            <div className="shipping_fee">
              <p className="heading">{t("confirmCvsPayment.postage")}</p>
              <p className="total">
                <span {...withNoTranslate({ className: "price_value" })}>
                  ¥{shippingFee.toLocaleString()}
                </span>
                <span>{t("confirmCvsPayment.taxIncluded")}</span>
              </p>
            </div>
          ) : null}
          {serviceFee ? (
            <div className="shipping_fee">
              <p className="heading">{t("confirmCvsPayment.serviceFee")}</p>
              <p className="total">
                <span {...withNoTranslate({ className: "price_value" })}>
                  ¥{serviceFee.toLocaleString()}
                </span>
                <span>{t("confirmCvsPayment.taxIncluded")}</span>
              </p>
            </div>
          ) : null}
          {settlementFee ? (
            <div className="shipping_fee">
              <p className="heading">
                {t("confirmCvsPayment.settlementSystemFees")}
              </p>
              <p className="total">
                <span {...withNoTranslate({ className: "price_value" })}>
                  ¥{settlementFee.toLocaleString()}
                </span>
                <span>{t("confirmCvsPayment.taxIncluded")}</span>
              </p>
            </div>
          ) : null}
          <div className="total_num">
            <p className="heading">{t("confirmCvsPayment.totalOfProducts")}</p>
            <p className="total">
              <span {...withNoTranslate({ className: "total_price_value" })}>
                ¥{totalPrice.toLocaleString()}
              </span>
              <span>{t("confirmCvsPayment.taxIncluded")}</span>
            </p>
          </div>

          {!!cvsCode && !!cvsNote && renderCvsInfo(cvsNote)}
          {phoneCode && renderPhoneInfo(status)}
          {paypalURL && renderPayPalInfo(status, errorInfo)}

          <LinkButton onClick={close}>
            {t("confirmCvsPayment.close")}
          </LinkButton>
        </div>
        <p
          onClick={close}
          aria-label={t("confirmCvsPayment.close")}
          {...withNoTranslate({ className: "modal_close" })}
        >
          x
        </p>
      </PaymentNumberContainer>
    </FullScreenDialog>
  );
};

export const ConfirmCvsPaymentView = withStyles(styles)(
  ConfirmCvsPaymentViewBase
);
